import { useState, useEffect, useCallback } from 'react';
import { StorageManager } from '../utils/storageManager';
import { DistributionStats } from '../types/types';

// Define interface for round data
export interface RoundData {
  roundNumber: number;
  actualYear: number;
  guessedYear: number;
  score: number;
}

// Define interface for local challenge stats (different from API ChallengeStats)
export interface LocalChallengeStats {
  score: number;
  correctGuesses: number;
  totalRounds: number;
  averageYearsOff: number;
}

interface GameStatsOptions {
  mode: 'daily';
  selectedDate?: string;
}

export const useGameStats = ({ mode, selectedDate }: GameStatsOptions) => {
  // For storing all guesses across rounds
  const [allGuesses, setAllGuessesState] = useState<
    Array<{ actualYear: number; guessedYear: number }>
  >([]);

  // For tracking scores per round
  const [pointsPerRound, setPointsPerRoundState] = useState<number[]>([]);

  // For showing the points gained in the most recent guess
  const [pointsGained, setPointsGained] = useState<number | null>(null);

  // Stats for display after completing the challenge
  const [challengeStats, setChallengeStats] = useState<LocalChallengeStats>({
    score: 0,
    correctGuesses: 0,
    totalRounds: 0,
    averageYearsOff: 0,
  });

  // Distribution stats returned from API
  const [distributionStats, setDistributionStats] = useState<DistributionStats>(
    {
      averageScore: 0,
      completions: 0,
      distributions: [],
      processedDistribution: undefined,
      percentileRank: undefined,
    }
  );

  // Track completion state
  const [completed, setCompleted] = useState(false);

  // Reset all stats
  const resetStats = useCallback(() => {
    setAllGuessesState([]);
    setPointsPerRoundState([]);
    setPointsGained(null);
    setChallengeStats({
      score: 0,
      correctGuesses: 0,
      totalRounds: 0,
      averageYearsOff: 0,
    });
    setDistributionStats({
      averageScore: 0,
      completions: 0,
      distributions: [],
      processedDistribution: undefined,
      percentileRank: undefined,
    });
    setCompleted(false);
  }, []);

  // Load stats for daily challenge from localStorage
  useEffect(() => {
    if (mode === 'daily') {
      const dateParam = selectedDate || StorageManager.getTodayDate();
      const isCompleted = localStorage.getItem(
        `yearGuess_completedDaily_${dateParam}`
      );

      if (isCompleted === 'true') {
        // Get stats from localStorage
        const scoreStr = localStorage.getItem(
          `yearGuess_dailyScores_${dateParam}`
        );
        const yearsOffStr = localStorage.getItem(
          `yearGuess_dailyYearsOff_${dateParam}`
        );
        const statsStr = localStorage.getItem(
          `yearGuess_dailyStats_${dateParam}`
        );
        const roundDataStr = localStorage.getItem(
          `yearGuess_dailyRoundData_${dateParam}`
        );

        // Set challenge stats
        setChallengeStats({
          score: scoreStr ? parseInt(scoreStr, 10) : 0,
          correctGuesses: 0, // Will be calculated from round data
          totalRounds: 0, // Will be set when challenge loaded
          averageYearsOff: yearsOffStr ? parseFloat(yearsOffStr) : 0,
        });

        // Parse and set distribution stats
        if (statsStr) {
          try {
            const parsedStats = JSON.parse(statsStr);

            // Check for old data format with count instead of density
            if (
              parsedStats &&
              parsedStats.processedDistribution &&
              parsedStats.processedDistribution.curvePoints &&
              parsedStats.processedDistribution.curvePoints.length > 0
            ) {
              const firstPoint =
                parsedStats.processedDistribution.curvePoints[0];
              // Check if it has 'count' but NOT 'density' (indicates old format)
              if (
                typeof firstPoint.count === 'number' &&
                typeof firstPoint.density === 'undefined'
              ) {
                // Discard the old structure so it doesn't cause type errors later
                parsedStats.processedDistribution = undefined;
              }
            }

            setDistributionStats(parsedStats);
          } catch (error) {}
        }

        // Load round data if available
        if (roundDataStr) {
          try {
            const parsedRoundData = JSON.parse(roundDataStr);
            setAllGuessesState(
              parsedRoundData.map((round: RoundData) => ({
                actualYear: round.actualYear,
                guessedYear: round.guessedYear,
              }))
            );
            setPointsPerRoundState(
              parsedRoundData.map((round: RoundData) => round.score)
            );

            // Count correct guesses
            const correct = parsedRoundData.filter(
              (round: RoundData) => round.actualYear === round.guessedYear
            ).length;
            setChallengeStats((prev) => ({ ...prev, correctGuesses: correct }));
          } catch (e) {}
        }

        setCompleted(true);
      } else {
        // Reset if not completed
        setCompleted(false);
        resetStats();
      }
    }
  }, [mode, selectedDate, resetStats]);

  // Record a guess
  const recordGuess = useCallback(
    (actualYear: number, guessedYear: number, points: number) => {
      setAllGuessesState((prev) => [...prev, { actualYear, guessedYear }]);
      setPointsPerRoundState((prev) => [...prev, points]);

      // Set pointsGained directly from points
      setPointsGained(points);

      return {
        allGuesses: [...allGuesses, { actualYear, guessedYear }],
        pointsPerRound: [...pointsPerRound, points],
      };
    },
    [allGuesses, pointsPerRound]
  );

  // Reset points gained display
  const resetPointsGained = useCallback(() => {
    setPointsGained(null);
  }, []);

  // New method to set points gained directly
  const setPointsGainedDirectly = useCallback((points: number | null) => {
    setPointsGained(points);
  }, []);

  // Calculate final stats
  const calculateFinalStats = useCallback(
    (totalRounds: number, score: number): LocalChallengeStats => {
      // Calculate the average years off properly using all guesses
      let totalYearsOff = 0;
      let correctGuesses = 0;

      allGuesses.forEach((guess) => {
        const yearsOff = Math.abs(guess.guessedYear - guess.actualYear);
        totalYearsOff += yearsOff;

        if (yearsOff === 0) {
          correctGuesses++;
        }
      });

      // Calculate average years off
      const averageYearsOff =
        allGuesses.length > 0 ? totalYearsOff / allGuesses.length : 0;

      const stats = {
        score,
        correctGuesses,
        totalRounds,
        averageYearsOff,
      };

      setChallengeStats(stats);
      setCompleted(true);

      return stats;
    },
    [allGuesses]
  );

  // Save daily challenge stats
  const saveDailyChallengeStats = useCallback(
    (apiStats: DistributionStats) => {
      if (!selectedDate) return;

      // Log the received API stats structure

      if (
        apiStats?.processedDistribution?.curvePoints &&
        apiStats.processedDistribution.curvePoints.length > 0
      ) {
      } else {
      }

      setDistributionStats(apiStats);
      localStorage.setItem(
        `yearGuess_dailyStats_${selectedDate}`,
        JSON.stringify(apiStats)
      );
    },
    [selectedDate]
  );

  // Renamed to avoid conflicts
  const updateAllGuesses = useCallback(
    (guesses: Array<{ actualYear: number; guessedYear: number }>) => {
      setAllGuessesState(guesses);
    },
    []
  );

  // Renamed to avoid conflicts
  const updatePointsPerRound = useCallback((points: number[]) => {
    setPointsPerRoundState(points);
  }, []);

  return {
    allGuesses,
    pointsPerRound,
    pointsGained,
    challengeStats,
    distributionStats,
    completed,
    recordGuess,
    resetPointsGained,
    calculateFinalStats,
    saveDailyChallengeStats,
    setDistributionStats,
    resetStats,
    updateAllGuesses,
    updatePointsPerRound,
    setPointsGainedDirectly, // New method
  };
};

export default useGameStats;
